'use client';
import { NFTX_API_URL, Network, config } from 'nftx.js';
import { NftxProvider as NftxCoreProvider, useOnEvent } from '@nftx/react';
import { useAddRecentTransaction } from '@rainbow-me/rainbowkit';
import { usePublicClient, useWalletClient } from 'wagmi';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// This component ties in nftx.js's transactions with rainbow's transaction history list
const NftxRainbowIntegration = ({ children }: { children: ReactNode }) => {
  const addRecentTransaction = useAddRecentTransaction();

  // When an event comes through for a new transaction we pass it to rainbow
  // Rainbow will then track and display info about it in the wallet popup
  useOnEvent('Mining', (e) => {
    // There has been an intermittent issue with addRecentTransaction that kills the entire app
    // I've experienced it twice but only on localhost so better safe than sorry
    try {
      if (e.transaction?.hash) {
        addRecentTransaction({
          hash: e.transaction.hash,
          description: e.description,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });

  return <>{children}</>;
};

const InvalidationThingy = ({ children }: { children: ReactNode }) => {
  const client = useQueryClient();
  useOnEvent('Success', (x) => {
    setTimeout(() => {
      client.invalidateQueries();
    }, 3000);
  });

  useEffect(() => {
    const h = setInterval(() => {
      if (config.internal.source === 'live') {
        client.invalidateQueries();
      }

      return () => clearInterval(h);
    }, 15000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
};

export default function NftxProvider({
  network,
  children,
}: {
  network: number;
  children: ReactNode;
}) {
  // This component must be rendered _inside_ the web3 provider so it can pull the provider/signer from wagmi
  const provider = usePublicClient({ chainId: network });
  const { data: signer } = useWalletClient({ chainId: network });
  const [rendered, setRendered] = useState(false);
  const onError = useCallback((e: any) => {
    console.error(e);
  }, []);

  if (!rendered) {
    config.configure({
      network,
      debug: true,
      keys: {
        RESERVOIR: {
          [Network.Mainnet]: '6037b5c4-02dd-5b86-97a9-f3eac2551ec5',
          [Network.Arbitrum]: '91896ab3-b729-5ecc-920f-94972dc60804',
          [Network.Sepolia]: 'f07cd839-ed50-5bdf-bc5e-c3e32d617b44',
          [Network.Base]: '7f1906e0-acaf-549e-a764-2870842787a8',
        },
        NFTX_API: process.env.NEXT_PUBLIC_NFTX_API_KEY, //'01GF46CVDK4TQ35330G3P6M6DC',
      },
      urls: {
        NFTX_API_URL: process.env.NEXT_PUBLIC_NFTX_API_URL || NFTX_API_URL,
      },
      subgraph: {
        NON_STANDARD_SUBGRAPH: {
          [Network.Mainnet]: 'https://g.nftx.xyz/non-standard-nfts/eth-mainnet',
        },
      },
    });
    setRendered(true);
  }

  return (
    <NftxCoreProvider
      network={network}
      provider={provider as any}
      signer={signer as any}
      onError={onError}
    >
      <NftxRainbowIntegration>
        <InvalidationThingy>{children}</InvalidationThingy>
      </NftxRainbowIntegration>
    </NftxCoreProvider>
  );
}
